import type { ApiRentalListingFull } from 'src/types/rental';

/**
 * Duration-banded rental pricing, renter side (rental-clock v2.1 spec §2). The dealer prices four
 * platform-fixed bands and a booking is charged at the band its length falls into — the storefront
 * shows the whole grid so the renter can see that a longer rental is cheaper per day.
 *
 * The exact amount of a booking is never computed here: that is a server quote
 * (`useRentalBookingQuote`). This file only labels the dealer's published rates.
 */
export interface RentalPricingBand {
  label: string;
  dailyPrice: number;
}

type PricedListing = Pick<
  ApiRentalListingFull,
  'tier1DailyPrice' | 'tier2DailyPrice' | 'tier3DailyPrice' | 'tier4DailyPrice' | 'dailyPrice'
>;

const BANDS = [
  { from: 1, to: 3, field: 'tier1DailyPrice' },
  { from: 4, to: 7, field: 'tier2DailyPrice' },
  { from: 8, to: 15, field: 'tier3DailyPrice' },
  { from: 16, to: null, field: 'tier4DailyPrice' },
] as const;

const bandLabel = (from: number, to: number | null) => (to === null ? `${from}+ days` : `${from}–${to} days`);

/**
 * The bands worth showing for a listing. Two collapses keep the card honest rather than busy:
 *
 *  - a listing served by an api that predates the grid (or one whose bands all cost the same)
 *    shows a single "Per day" line — there is nothing to compare;
 *  - neighbouring bands at the same price are merged, so one price never occupies two rows.
 */
export const describePricingBands = (listing: PricedListing | null | undefined): RentalPricingBand[] => {
  if (!listing) return [];

  const priced = BANDS.map((band) => ({ ...band, dailyPrice: listing[band.field] })).filter(
    (band): band is (typeof BANDS)[number] & { dailyPrice: number } => typeof band.dailyPrice === 'number'
  );

  const isFlat = priced.every((band) => band.dailyPrice === priced[0]?.dailyPrice);

  if (priced.length !== BANDS.length || isFlat) {
    return [{ label: 'Per day', dailyPrice: priced[0]?.dailyPrice ?? listing.dailyPrice }];
  }

  const merged = priced.reduce<{ from: number; to: number | null; dailyPrice: number }[]>((rows, band) => {
    const previous = rows[rows.length - 1];

    if (previous && previous.dailyPrice === band.dailyPrice) {
      previous.to = band.to;
      return rows;
    }

    return [...rows, { from: band.from, to: band.to, dailyPrice: band.dailyPrice }];
  }, []);

  return merged.map((row) => ({ label: bandLabel(row.from, row.to), dailyPrice: row.dailyPrice }));
};

/** The headline price of a listing card: the cheapest published band, marked "from" when the grid varies. */
export const fromDailyPrice = (listing: PricedListing): { price: number; isFrom: boolean } => {
  const bands = describePricingBands(listing);

  if (bands.length <= 1) {
    return { price: bands[0]?.dailyPrice ?? listing.dailyPrice, isFrom: false };
  }

  return { price: Math.min(...bands.map((band) => band.dailyPrice)), isFrom: true };
};
